/**
 * Evidence — show view on the shared Inspector grammar (design-system §5).
 *
 * Header carries the polarity badge + the Evidence named primary actions (set
 * polarity/weight · accept/reject — {@link EvidenceActions}). Overview lays out
 * the typed fields with a confidence meter; every `*_id` source column resolves
 * to a RefLink. Relations group the backing Claim (RefLink) and the source
 * Event / Document / Signal / Artifact (coverage §2). Body renders the evidence
 * text as markdown.
 */
import { useShow, useGo, useNavigation, type HttpError } from "@refinedev/core";
import {
  Box,
  FileText,
  Pencil,
  Radio,
  ScrollText,
  Zap,
} from "@/components/ui/icon";
import type { Evidence } from "@aos/shared";

import { Markdown } from "@/components/app/MarkdownView";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  type InspectorAction,
} from "@/components/inspector";
import { humanize } from "@/lib/format";
import { formatDateTime, orDash } from "../_shared/runs-ui";

import { PolarityBadge } from "./_polarity";
import { EvidenceActions } from "./EvidenceActions";

/** Compact confidence meter for a 0..1 score. */
function ConfidenceMeter({ value }: { value: number | null }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">Not scored</span>;
  }
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-xs text-muted-foreground">{pct}%</span>
    </div>
  );
}

export function EvidenceShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Evidence, HttpError>({ resource: "evidence" });
  const { data, isLoading, isError, error, refetch } = query;
  const record = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !record) {
    return (
      <ShowPageError
        message={error?.message ?? "Evidence not found."}
        onRetry={() => void refetch()}
      />
    );
  }

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: editUrl("evidence", record.id) }),
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {record.title}
            <PolarityBadge polarity={record.polarity} />
          </span>
        }
        subtitle="A piece of evidence supporting or contradicting a claim — its polarity, weight, and source."
        primaryAction={<EvidenceActions evidence={record} />}
        menuActions={menuActions}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Evidence type">{humanize(record.evidence_type)}</Field>
          <Field label="Polarity">
            <PolarityBadge polarity={record.polarity} />
          </Field>
          <Field label="Weight">{orDash(record.weight)}</Field>
          <Field label="Confidence">
            <ConfidenceMeter value={record.confidence_score} />
          </Field>
          <Field label="Claim type">
            {record.claim_type ? humanize(record.claim_type) : null}
          </Field>
          <Field label="Created by">
            <RefLink resource="users" id={record.created_by_user_id} />
          </Field>
          <Field label="Created">{formatDateTime(record.created_at)}</Field>
          {record.interpretation ? (
            <Field label="Interpretation" full>
              <Markdown source={record.interpretation} />
            </Field>
          ) : null}
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        {record.body ? (
          <Markdown source={record.body} />
        ) : (
          <p className="text-sm text-muted-foreground">No body.</p>
        )}
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Backing claim",
              items: [
                {
                  label: "Claim",
                  icon: ScrollText,
                  resource: "claims",
                  id: record.claim_id,
                },
              ],
            },
            {
              title: "Sources",
              items: [
                {
                  label: "Artifact",
                  icon: Box,
                  resource: "artifacts",
                  id: record.source_artifact_id ?? record.artifact_id,
                },
                {
                  label: "Signal",
                  icon: Radio,
                  resource: "signals",
                  id: record.source_signal_id,
                },
                {
                  label: "Event",
                  icon: Zap,
                  resource: "events",
                  id: record.source_event_id,
                },
                {
                  label: "Document",
                  icon: FileText,
                  resource: "documents",
                  id: record.source_document_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={record.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="evidence" entityId={record.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default EvidenceShow;
